import { useState } from 'react';
import { Box } from '@mui/material';
import {
  CasChip,
  CasRenderArrayAsRows,
  CasTable,
  CasErrorDisplay,
} from 'src/compass/shared/components';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import {
  CasRiskManagerSelect,
  CasTagMultiSelect,
  CasYesNoSelect,
} from 'src/compass/application';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';

import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import sanitizeSearchText from './_internal/sanitizeSearchText';
import CustomerDialog from './CustomerDialog';
import { RenderRiskManagerName } from './RenderRiskManagerName';
import { RenderLastTouchpoint } from './RenderLastTouchpoint';
import { useTouchpointFrequencyOptions } from './TouchpointFrequencySelect';
import { TOUCHPOINT_ENTITY } from './TouchpointsTab';

export default function CustomersTab() {
  const [dialogRowId, setDialogRowId] = useState<number | null>(null);
  const [reloadTable, setReloadTable] = useState<Date | null>(null);

  const {
    data: riskManagerData,
    isLoading,
    isError,
    error,
  } = useRetrieve({ url: 'CurrentUser/RiskManager' });

  // Frequency lookup: { id: Days, name: Description }. Used in the
  // Last Touchpoint cell to surface the Description for entities that have
  // no last-touchpoint date but a "0 Days" (excluded) frequency.
  const { options: freqOptions } = useTouchpointFrequencyOptions({});

  if (isLoading) return <CasLoader />;
  if (isError) return <CasErrorDisplay message={error.message} />;

  return (
    <>
      <CasTable
        entity={TOUCHPOINT_ENTITY}
        includeExport={false}
        refreshBtn
        update={reloadTable}
        columns={[
          {
            field: 'name',
            render: (props: ICasRenderCellProps) => (
              <RenderRiskManagerName {...props} onClick={setDialogRowId} />
            ),
          },
          {
            field: 'contacts',
            sortable: false,
            render: CasRenderArrayAsRows,
          },
          {
            field: 'tags',
            sortable: false,
            render: ({ row }: ICasRenderCellProps) =>
              (row.tags as string[] | undefined)?.map((tag, index) => (
                <Box key={`${index}${tag}`} sx={{ mb: 0.25 }}>
                  <CasChip label={tag} size="small" color="lightBlue" />
                </Box>
              )),
          },
          {
            field: 'lastTouchpointDate',
            label: 'Last Touchpoint',
            render: (props: ICasRenderCellProps) => (
              <RenderLastTouchpoint {...props} freqOptions={freqOptions} />
            ),
          },
        ]}
        filtersInDrawer
        /*
          All filters live in the Filters drawer. `isPrimaryFilter` ones also stay
          on the toolbar for quick access (they share the drawer's value, so either
          copy stays in sync, and they get no chip since they're already visible).
          Tags is a multi-select matching ANY selected tag.
        */
        filters={[
          {
            type: 'component',
            name: 'employeeId',
            label: 'RMC',
            isPrimaryFilter: true,
            initialValue: riskManagerData?.riskManagerId,
            // Reset restores the current user rather than clearing: the API rejects a
            // null employeeId for BusDev users.
            resetValue: riskManagerData?.riskManagerId,
            component: CasRiskManagerSelect,
            // All = every customer regardless of RMC, so it is main-office only; a branch
            // user is scoped to their own people, and BusDev to themselves. The API applies
            // the same rule (EntityTouchpointService.GetList) rather than trusting this.
            includeAllOption: !!riskManagerData?.isMainOffice && !riskManagerData?.isBusDev,
          },
          {
            name: 'searchName',
            label: 'Name',
            isPrimaryFilter: true,
            autoFocus: true,
            // This search runs three full-text branches plus the paging count, so it
            // waits for a pause in typing rather than firing per keystroke - and longer
            // than the app default, which split normal typing into several requests here.
            debounce: 400,
            // Full-text operators are refused at entry (with a snackbar saying which)
            // instead of being typed and then silently dropped server-side, where an
            // `&` makes the whole AND-joined search match nothing.
            sanitize: sanitizeSearchText,
          },
          {
            type: 'component',
            name: 'tagIds',
            label: 'Tags',
            component: CasTagMultiSelect,
          },
          {
            type: 'component',
            name: 'overdueOnly',
            label: 'Touchpoint Overdue',
            includeAllOption: true,
            component: CasYesNoSelect,
          },
          {
            type: 'component',
            name: 'lrpHistory',
            label: 'LRP History?',
            includeAllOption: true,
            component: CasYesNoSelect,
          },
          {
            type: 'component',
            name: 'cmeHistory',
            label: 'CME History?',
            includeAllOption: true,
            component: CasYesNoSelect,
          },
          {
            type: 'number',
            name: 'lrpQuoteWithinDays',
            label: 'LRP Quote Click Within (Days)',
          },
          {
            name: 'phone',
            label: 'Phone',
          },
        ]}
      />
      {dialogRowId && (
        <CustomerDialog
          id={dialogRowId}
          onClose={(dataUpdated?: boolean) => {
            setDialogRowId(null);
            if (dataUpdated) setReloadTable(new Date());
          }}
        />
      )}
    </>
  );
}
